import type { ReactNode } from 'react';
import { Tabs as RTabs } from 'radix-ui';
import { cn } from '../../lib/cn';

export interface TabItem {
  value: string;
  label: ReactNode;
  content: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Accessible name for the tab list. */
  label: string;
  className?: string;
}

/** Accessible tabs (arrow keys move between tabs). For navigation between pages use links instead. */
export function Tabs({ items, value, defaultValue, onValueChange, label, className }: TabsProps) {
  return (
    <RTabs.Root
      value={value}
      defaultValue={defaultValue ?? items[0]?.value}
      onValueChange={onValueChange}
      // min-w-0: let the tab list scroll sideways instead of widening the page on phones.
      className={cn('flex min-w-0 max-w-full flex-col gap-4', className)}
    >
      <RTabs.List aria-label={label} className="flex gap-1 overflow-x-auto border-b-2 border-border">
        {items.map((t) => (
          <RTabs.Trigger
            key={t.value}
            value={t.value}
            disabled={t.disabled}
            className={cn(
              '-mb-[2px] min-h-11 shrink-0 border-b-4 border-transparent px-4 font-bold whitespace-nowrap text-muted',
              'hover:text-text data-[state=active]:border-primary data-[state=active]:text-primary disabled:opacity-50',
            )}
          >
            {t.label}
          </RTabs.Trigger>
        ))}
      </RTabs.List>
      {items.map((t) => (
        <RTabs.Content key={t.value} value={t.value} className="focus-visible:outline-offset-4">
          {t.content}
        </RTabs.Content>
      ))}
    </RTabs.Root>
  );
}
